<template>
  <div class="fixed inset-0 flex items-center justify-center bg-black/80 z-[999] px-4 backdrop-blur-sm">
    <div
      class="relative w-full max-w-md bg-[#14181F] border border-gray-800 rounded-[16px] p-8 shadow-[0_10px_40px_rgba(0,0,0,0.5)] text-center font-sans overflow-hidden"
    >
      <img
        src="@/assets/guess-modal-bg.png"
        alt="Card Background"
        class="absolute inset-0 w-full h-full object-cover sm:object-contain rounded-[16px] opacity-10 pointer-events-none z-0"
      />

      <button
        @click.stop="closeModal"
        class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors z-20"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div class="relative z-10 w-full mt-2">
        <h2 class="text-2xl sm:text-3xl font-black text-[#00F0FF] font-oxanium tracking-wide uppercase mb-4 mt-2">
          {{
            success
              ? $t('AddGuessModal.TitleSuccess')
              : errorMessage && !inputError
              ? $t('AddGuessModal.TitleFailed')
              : $t('AddGuessModal.TitleDefault')
          }}
        </h2>

        <!-- Success Animation -->
        <div v-if="success" class="flex flex-col items-center mt-6 min-h-[150px] justify-center px-4">
          <div class="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00F0FF]/10 text-[#00F0FF]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p class="text-[#B9CACB] text-sm sm:text-base leading-relaxed">
            {{ $t('AddGuessModal.SuccessMessage') }}
          </p>
        </div>

        <!-- Error Animation (For API errors) -->
        <div
          v-else-if="errorMessage && !inputError"
          class="flex flex-col items-center mt-6 min-h-[150px] justify-center px-4"
        >
          <div class="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 text-red-500">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <p class="text-[#B9CACB] text-sm sm:text-base leading-relaxed">
            {{ errorMessage ? errorMessage : "Sorry, Something Went Wrong" }}
          </p>
        </div>

        <!-- Input Fields -->
        <div v-else-if="isLoggedIn">
          <p class="text-[#B9CACB] text-sm sm:text-base leading-relaxed mb-6">
            {{ $t('AddGuessModal.OnlyPositiveNumbers') }}
          </p>

          <!-- GTB Guess -->
          <div class="w-full flex flex-col justify-center items-start mt-4">
            <label class="text-[#B9CACB] font-semibold mb-2 block text-xs uppercase tracking-wider">{{ $t('AddGuessModal.LabelGTB') }}</label>
            <input
              v-model="guess"
              type="number"
              min="0"
              :placeholder="inputError || ''"
              :class="[
                'w-full bg-[#0D0B10] border rounded-[10px] px-4 py-3 text-sm text-white focus:outline-none transition-colors mb-2',
                inputError ? 'border-red-500/50 focus:border-red-500 placeholder-red-500' : 'border-gray-800 placeholder-gray-500 focus:border-[#00F0FF]',
              ]"
              @input="validateGuess"
            />
          </div>

          <!-- GTHP Guess -->
          <div class="w-full flex flex-col justify-center items-start mt-4">
            <label class="text-[#B9CACB] font-semibold mb-2 block text-xs uppercase tracking-wider">{{ $t('AddGuessModal.LabelGTHP') }}</label>
            <input
              v-model="gthpGuess"
              type="number"
              min="0"
              :placeholder="gthpInputError || ''"
              :class="[
                'w-full bg-[#0D0B10] border rounded-[10px] px-4 py-3 text-sm text-white focus:outline-none transition-colors mb-4',
                gthpInputError
                  ? 'border-red-500/50 focus:border-red-500 placeholder-red-500'
                  : 'border-gray-800 placeholder-gray-500 focus:border-[#00F0FF]',
              ]"
              @input="validateGuess"
            />
          </div>

          <div class="flex w-full mt-2">
            <button
              @click="submitGuess"
              :disabled="loading"
              class="w-full px-6 py-3 bg-[#00F0FF] text-black font-bold text-sm rounded-[10px] hover:bg-[#00d4e0] transition-colors uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{
                loading
                  ? $t('AddGuessModal.Submitting')
                  : `${$t('AddGuessModal.SubmitGuessFor')} ${requiredPoints} ${
                      requiredPoints === 1 ? $t('AddGuessModal.Point') : $t('AddGuessModal.Points')
                    }`
              }}
            </button>
          </div>
        </div>

        <!-- If user is NOT logged in -->
        <div v-else class="text-center w-full mt-6">
          <div class="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 text-red-500">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <p class="text-[#B9CACB] text-sm sm:text-base leading-relaxed mb-6">
            {{ $t('AddGuessModal.NeedLogin') }}
          </p>

          <div class="flex gap-3 w-full">
            <button
              @click="closeModal"
              class="w-full px-6 py-3 bg-[#1E2535] text-white font-bold text-sm rounded-[10px] hover:bg-gray-700 transition-colors uppercase tracking-wider"
            >
              {{ $t('AddGuessModal.Close') }}
            </button>
            <button
              @click="handleLogin"
              class="w-full px-6 py-3 bg-[#00F0FF] text-black font-bold text-sm rounded-[10px] hover:bg-[#00d4e0] transition-colors uppercase tracking-wider"
            >
              {{ $t('AddGuessModal.Login') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    sessionId: {
      type: String,
      required: true,
    },
    requiredPoints: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      guess: "",
      gthpGuess: "",
      loading: false,
      success: false,
      errorMessage: "",
      inputError: "",
      gthpInputError: "",
    };
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem("maherco-access-token");
    },
  },
  methods: {
    async submitGuess() {
      const trimmedGuess = this.guess.toString().trim();
      const trimmedGTHP = this.gthpGuess.toString().trim();

      if (!trimmedGuess || isNaN(trimmedGuess)) {
        this.inputError = "Please enter a valid GTB guess.";
        return;
      }

      if (!trimmedGTHP || isNaN(trimmedGTHP)) {
        this.gthpInputError = "Please enter a valid GTHP guess.";
        return;
      }

      this.loading = true;
      this.inputError = "";
      this.gthpInputError = "";
      this.errorMessage = "";

      try {
        const token = localStorage.getItem("maherco-access-token");
        if (!token) {
          this.showError("You need to log in first.");
          return;
        }

        if (!this.sessionId) {
          this.showError("Session ID missing.");
          return;
        }

        await axios.post(
          `${process.env.VUE_APP_BASE_API}/api/guess-the-balance/guess`,
          {
            gtb: this.sessionId,
            guess: parseInt(trimmedGuess, 10),
            gthpGuess: parseInt(trimmedGTHP, 10),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        this.success = true;
        this.guess = "";
        this.gthpGuess = "";
        this.$emit("guessSubmitted");
        this.autoClose();
      } catch (error) {
        const msg = error.response?.data?.message || "Failed to submit guess";
        this.showError(msg);

        if (msg === "GTB guessing has ended.") {
          this.$emit("gtbEnded");
        }
      } finally {
        this.loading = false;
      }
    },
    validateGuess() {
      if (this.guess < 0) {
        this.guess = "";
        this.inputError = "Negative values are not allowed.";
      } else {
        this.inputError = "";
      }

      if (this.gthpGuess < 0) {
        this.gthpGuess = "";
        this.gthpInputError = "Negative values are not allowed.";
      } else {
        this.gthpInputError = "";
      }
    },
    showError(message) {
      this.errorMessage = message;
      this.inputError = "";
      this.gthpInputError = "";
      this.autoClose();
    },
    autoClose() {
      setTimeout(() => {
        this.closeModal();
      }, 5000);
    },
    closeModal() {
      this.success = false;
      this.errorMessage = "";
      this.inputError = "";
      this.gthpInputError = "";
      this.$emit("close");
    },
  },
};
</script>
